import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BoxArrowUpRight,
  CheckCircleFill,
  Clipboard,
  ExclamationTriangleFill,
  PlayFill,
  Search,
  XCircleFill,
} from "react-bootstrap-icons";
import { getDeviceMqttTopics } from "../commonApi";
import { Loader } from "../Loader";

// MQTT topics of one device (GET /things/{thingName}/mqtt-topics), shown like the AWS IoT console:
// name, action, topic. Topics the hub uses are also checked against its IoT policies; only problems are shown.

const GROUPS = [
  ["SHADOW", "Shadow"],
  ["COMMANDS", "Commands"],
  ["OTA", "OTA"],
  ["REQUESTS", "Hub requests"],
  ["PROVISIONING", "Provisioning & logs"],
  ["TERMINAL", "Remote terminal"],
];

const PROBLEM = new Set(["NOT_ALLOWED", "DENIED", "UNKNOWN"]);

export const testClientUrl = (thingName, { sub = [], pub } = {}) => {
  const qs = new URLSearchParams();
  if (thingName) qs.set("thing", thingName);
  sub.forEach((s) => qs.append("sub", s));
  if (pub) qs.set("pub", pub);
  return `/devices/mqtt-test-client?${qs.toString()}`;
};

function MqttTopicsTab({ thingName, reloadKey, onCopy }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [group, setGroup] = useState("ALL");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDeviceMqttTopics(thingName)
      .then((res) => !cancelled && setData(res.data?.data || null))
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        setError(
          e.response?.status === 403
            ? "You need the ADMIN role to see device MQTT topics"
            : e.response?.data?.error?.message || e.message
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [thingName, reloadKey]);

  const topics = useMemo(() => data?.topics || [], [data]);
  const problems = topics.filter((t) => PROBLEM.has(t.policyStatus));
  const checked = topics.filter((t) => t.policyStatus && t.policyStatus !== "NOT_CHECKED").length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return topics.filter(
      (t) => (group === "ALL" || t.group === group) && (!q || `${t.name} ${t.topic}`.toLowerCase().includes(q))
    );
  }, [topics, group, query]);

  if (loading) {
    return (
      <div className="spin-loader">
        <Loader />
      </div>
    );
  }
  if (error) return <div className="dhc-alert dhc-alert-fail">{error}</div>;
  if (!data) return <div className="dhc-empty">No MQTT topic data</div>;

  const test = (t) =>
    navigate(
      testClientUrl(thingName, t.action === "Publish" ? { pub: t.topic, sub: [] } : { sub: [t.subscribeFilter] })
    );

  return (
    <>
      <div className="dhc-section-head">
        <div>
          <h3>MQTT topics</h3>
          <p className="dhc-muted">
            Topics this {data.child ? "device and its hub" : "device"} use
            {data.child && (
              <>
                {" "}
                · MQTT connection held by hub <span className="dhc-mono">{data.hubThingName}</span>
              </>
            )}
          </p>
        </div>
        <button type="button" className="dhc-btn dhc-btn-outline" onClick={() => navigate(testClientUrl(thingName))}>
          MQTT test client <BoxArrowUpRight size={12} />
        </button>
      </div>

      {data.policyError ? (
        <div className="dhc-alert dhc-alert-warn">
          <ExclamationTriangleFill style={{ marginTop: 3, flexShrink: 0 }} />
          <span>{data.policyError}</span>
        </div>
      ) : problems.length ? (
        <div className="dhc-alert dhc-alert-fail">
          <XCircleFill style={{ marginTop: 3, flexShrink: 0 }} />
          <div>
            <b>
              {problems.length} topic{problems.length > 1 ? "s" : ""} not allowed by the certificate's policies (
              {data.policies.join(", ") || "none"})
            </b>
            {problems.map((t) => (
              <div key={t.topic} className="dhc-mono" style={{ marginTop: 2 }}>
                {t.topic} — {t.permissions.filter((p) => PROBLEM.has(p.status)).map((p) => p.action).join(", ")}
              </div>
            ))}
          </div>
        </div>
      ) : (
        checked > 0 && (
          <div className="dhc-policy-ok">
            <CheckCircleFill /> All {checked} topics the hub uses are allowed by {data.policies.join(", ")}
          </div>
        )
      )}

      <div className="dhc-toolbar">
        <div className="dhc-segment dhc-segment-scroll">
          {[["ALL", "All"], ...GROUPS].map(([key, label]) => (
            <button key={key} type="button" className={group === key ? "is-active" : ""} onClick={() => setGroup(key)}>
              {label}
            </button>
          ))}
        </div>
        <div className="dhc-search-field">
          <Search size={13} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter topics" />
        </div>
      </div>

      <div className="dhc-table-wrap">
        <table className="dhc-table dhc-topic-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Action</th>
              <th>MQTT topic</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {GROUPS.map(([key, label]) => {
              const rows = visible.filter((t) => t.group === key);
              if (!rows.length) return null;
              return (
                <React.Fragment key={key}>
                  {group === "ALL" && (
                    <tr className="dhc-group-row">
                      <td colSpan={4}>{label}</td>
                    </tr>
                  )}
                  {rows.map((t) => {
                    const problem = PROBLEM.has(t.policyStatus);
                    return (
                      <tr key={t.topic} className={problem ? "is-problem" : ""}>
                        <td className="dhc-mono dhc-nowrap">
                          {problem && (
                            <XCircleFill
                              className="dhc-problem-icon"
                              title={t.permissions.map((p) => `${p.action}: ${p.reason}`).join("\n")}
                            />
                          )}
                          {t.name}
                          {t.scope === "HUB" && <span className="dhc-tag">Hub</span>}
                        </td>
                        <td>{t.action}</td>
                        <td>
                          <span className="dhc-mono">{t.topic}</span>
                        </td>
                        <td className="dhc-row-actions">
                          <button type="button" className="dhc-icon-btn" title="Copy topic" onClick={() => onCopy(t.topic)}>
                            <Clipboard size={13} />
                          </button>
                          <button
                            type="button"
                            className="dhc-icon-btn"
                            title={t.action === "Publish" ? "Publish in test client" : "Subscribe in test client"}
                            onClick={() => test(t)}
                          >
                            <PlayFill size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {!visible.length && (
              <tr>
                <td colSpan={4} className="dhc-muted">
                  No topics match the filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default MqttTopicsTab;
